// EXPORT BUTTON COMPONENT
// Reusable export button with format selection (CSV/Excel).
// Phase 7B: Employee & Export

import React, {useState} from 'react'
import {Button, Dropdown, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {exportReport} from '../../../../api/ReportsAPI'

interface ExportButtonProps {
  reportType: 'team-utilization' | 'project-analysis' | 'employee-productivity' | 'approval-analytics'
  filters: {
    startDate: string
    endDate: string
    managerId?: string
    userId?: string
    projectCodeId?: string
    projectType?: string
    includeLeave?: boolean
  }
  disabled?: boolean
}

const ExportButton: React.FC<ExportButtonProps> = ({reportType, filters, disabled = false}) => {
  const [loading, setLoading] = useState(false)

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setLoading(true)
    try {
      // Call export API
      const blob = await exportReport({
        reportType,
        format,
        ...filters,
      })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Generate filename
      const reportName = reportType.replace(/-/g, '_')
      const date = new Date().toISOString().split('T')[0]
      link.download = `${reportName}_${date}.${format}`
      
      // Trigger download
      document.body.appendChild(link)
      link.click()
      
      // Cleanup
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success(`Report exported successfully as ${format.toUpperCase()}`)
    } catch (error: any) {
      console.error('Export error:', error)
      toast.error(error?.response?.data?.message || 'Failed to export report')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dropdown>
      <Dropdown.Toggle
        variant='success'
        size='sm'
        disabled={disabled || loading}
        className='d-flex align-items-center gap-2'
      >
        {loading ? (
          <>
            <Spinner animation='border' size='sm' />
            <span>Exporting...</span>
          </>
        ) : (
          <>
            <i className='fas fa-download'></i>
            <span>Export</span>
          </>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <Dropdown.Item onClick={() => handleExport('csv')} disabled={loading}>
          <i className='fas fa-file-csv me-2'></i>
          Export as CSV
        </Dropdown.Item>
        <Dropdown.Item onClick={() => handleExport('xlsx')} disabled={loading}>
          <i className='fas fa-file-excel me-2'></i>
          Export as Excel
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default ExportButton

